import React, { useState, useRef, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import CabecalhoEditavel from './CabecalhoEditavel';
import BlocoRenderer from './BlocoRenderer';
import { BLOCK_TYPES, novoBloco } from './blockTypes';
import { PROPOSTAS_DESIGN } from '../Propostas.design';
import { DOCUMENTO_DESIGN as D } from '../EditorDocumento.design';

export default function DocumentCanvas({ proposta, onChange, readOnly, isModelo }) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);
  const blocos = proposta.blocos || [];

  useEffect(() => {
    const handleClick = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const updateLayout = (patch) => onChange({ layout: { ...proposta.layout, ...patch } });

  const updateBloco = (index, patch) => {
    onChange({ blocos: blocos.map((b, i) => i === index ? { ...b, ...patch } : b) });
  };

  const removeBloco = (index) => {
    onChange({ blocos: blocos.filter((_, i) => i !== index).map((b, i) => ({ ...b, ordem: i })) });
  };

  const moveBloco = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= blocos.length) return;
    const copy = [...blocos];
    [copy[index], copy[j]] = [copy[j], copy[index]];
    onChange({ blocos: copy.map((b, i) => ({ ...b, ordem: i })) });
  };

  const addBloco = (tipo) => {
    setShowAddMenu(false);
    onChange({ blocos: [...blocos, novoBloco(tipo, blocos.length)] });
  };

  const temItens = blocos.some(b => b.tipo === 'itens' || b.tipo === 'tabela_precos');

  return (
    <div className={PROPOSTAS_DESIGN.editor.canvas}>
      <CabecalhoEditavel
        layout={proposta.layout}
        onChangeLayout={updateLayout}
        readOnly={readOnly}
      />

      {/* Título como campo silencioso, não como <Input>: o componente base aplica
          md:text-sm por último e derrubava o título para 14px. */}
      {readOnly ? (
        <h1 className="text-2xl font-bold text-gray-900 dark:text-[#D1D2D3] mb-6">{proposta.titulo}</h1>
      ) : (
        <input
          value={proposta.titulo || ''}
          onChange={e => onChange({ titulo: e.target.value })}
          placeholder="Título da proposta"
          aria-label="Título da proposta"
          className={`${D.docInput} ${D.docTitle}`}
        />
      )}

      {blocos.map((bloco, index) => (
        <BlocoRenderer
          key={index}
          bloco={bloco}
          proposta={proposta}
          onChange={patch => updateBloco(index, patch)}
          onChangeProposta={onChange}
          onRemove={() => removeBloco(index)}
          onMoveUp={() => moveBloco(index, -1)}
          onMoveDown={() => moveBloco(index, 1)}
          readOnly={readOnly}
          isModelo={isModelo}
          isFirst={index === 0}
          isLast={index === blocos.length - 1}
        />
      ))}

      {!readOnly && (
        <div className="relative mt-4" ref={addMenuRef}>
          <button type="button" onClick={() => setShowAddMenu(v => !v)} className={D.addBlock}>
            <Iconify icon="eva:plus-fill" width={16} />
            Adicionar seção
          </button>

          {showAddMenu && (
            <div className={D.addMenu}>
              {BLOCK_TYPES.map(bt => {
                const disabled = bt.unique && bt.tipo === 'itens' && temItens;
                return (
                  <button
                    key={bt.tipo}
                    type="button"
                    disabled={disabled}
                    onClick={() => addBloco(bt.tipo)}
                    className={D.addMenuItem}
                  >
                    <div className={`${D.paletteIcon} mt-0.5`}>
                      <Iconify icon={bt.icon} width={15} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{bt.label}</p>
                      <p className="text-[11px] text-gray-500">{disabled ? 'Já adicionado' : bt.descricao}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(proposta.layout?.rodape_texto || !readOnly) && (
        <div className="mt-8 pt-4 border-t border-gray-100 dark:border-[#383838]">
          {readOnly ? (
            <p className="text-xs text-gray-400">{proposta.layout?.rodape_texto}</p>
          ) : (
            <input
              value={proposta.layout?.rodape_texto || ''}
              onChange={e => updateLayout({ rodape_texto: e.target.value })}
              placeholder="Texto de rodapé (ex.: agradecimento, contato)"
              aria-label="Texto de rodapé"
              className={`${D.docInput} w-full text-xs !text-gray-500`}
            />
          )}
        </div>
      )}
    </div>
  );
}
